import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Grid,
  TextField,
  Button,
  MenuItem,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { simplBookingService } from '@/services/bookingService';
import { format } from 'date-fns';
import { safeFormatDate } from '@/utils/filterUtils';

interface EditBookingModalProps {
  open: boolean;
  onClose: () => void;
  bookingId: string | null;
  onSuccess: () => void;
}

const modalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: '80%', md: '600px' },
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  maxHeight: '90vh',
  overflowY: 'auto',
};

export const EditBookingModal: React.FC<EditBookingModalProps> = ({ open, onClose, bookingId, onSuccess }) => {
  const [booking, setBooking] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editReason, setEditReason] = useState<string>('');

  useEffect(() => {
    if (open && bookingId) {
      setIsLoading(true);
      setError(null);
      setEditReason(''); // Reset reason when opening modal

      // Fetch booking by ID directly
      simplBookingService.getBookingById(bookingId)
        .then(foundBooking => {
          if (!foundBooking) {
            throw new Error('Booking data is null or undefined');
          }
          setBooking({
            ...foundBooking,
            startDate: safeFormatDate(foundBooking.startDate, "yyyy-MM-dd'T'HH:mm", ''),
            endDate: safeFormatDate(foundBooking.endDate, "yyyy-MM-dd'T'HH:mm", ''),
            totalAmount: foundBooking.amount || foundBooking.totalAmount || 0,
          });
        })
        .catch(err => {
          const errorMessage = err.message || 'Failed to fetch booking details. Please try again.';
          console.error('❌ EditBookingModal - Error fetching booking:', err);
          setError(errorMessage);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else if (open && !bookingId) {
      console.error('❌ EditBookingModal opened without bookingId!');
      setError('No booking ID provided');
      setIsLoading(false);
    }
  }, [open, bookingId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setBooking({ ...booking, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    if (!bookingId) return;

    // Validate edit reason is provided
    if (!editReason.trim()) {
      setError('Please provide a reason for modifying this booking');
      return;
    }

    setIsSaving(true);
    setError(null);
    try {

      // Prepare update data with only the fields that should be updated
      const updateData: any = {
        status: booking.status,
        totalAmount: parseFloat(booking.totalAmount) || parseFloat(booking.amount) || 0,
      };

      // Include dates if they're provided and valid
      if (booking.startDate) {
        updateData.startDate = new Date(booking.startDate).toISOString();
      }
      if (booking.endDate) {
        updateData.endDate = new Date(booking.endDate).toISOString();
      }

      // Include type/serviceType if changed
      if (booking.type || booking.serviceType) {
        updateData.type = booking.type || booking.serviceType;
      }

      // Include edit justification/reason
      updateData.modificationReason = editReason;
      updateData.adminNotes = `Modified by admin: ${editReason}`;

      // Call the update API
      await simplBookingService.updateBooking(bookingId, updateData);

      onSuccess();
      onClose();
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to save changes. Please try again.';
      setError(errorMessage);
      console.error('Error saving booking:', err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">
            Edit Booking
          </Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
        {isLoading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date"
                name="startDate"
                type="datetime-local"
                value={booking.startDate || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date"
                name="endDate"
                type="datetime-local"
                value={booking.endDate || ''}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="Booking Type"
                name="type"
                value={booking.type || booking.serviceType || ''}
                onChange={handleChange}
              >
                {['rental', 'driver-hire', 'agent-assisted', 'instant', 'self-drive', 'with-driver', 'request-to-book', 'chauffeur-driven', 'hybrid'].map(option => (
                  <MenuItem key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1).replace('-', ' ')}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Status"
                name="status"
                value={booking.status || 'pending'}
                onChange={handleChange}
              >
                {['pending', 'confirmed', 'active', 'completed', 'cancelled'].map(option => (
                  <MenuItem key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Total Amount"
                name="totalAmount"
                type="number"
                value={booking.totalAmount || booking.amount || 0}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Reason for Modification (Required)"
                placeholder="Please explain why you are modifying this booking..."
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                required
                error={error === 'Please provide a reason for modifying this booking'}
                helperText={error === 'Please provide a reason for modifying this booking' ? error : 'This will be logged for audit purposes'}
              />
            </Grid>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button onClick={onClose} color="inherit">Cancel</Button>
              <Button onClick={handleSave} variant="contained" disabled={isSaving || !editReason.trim()}>
                {isSaving ? <CircularProgress size={24} /> : 'Save Changes'}
              </Button>
            </Grid>
          </Grid>
        )}
      </Box>
    </Modal>
  );
};